import { Controller } from '@hotwired/stimulus'

// Drives the bulk-selection toolbar on the /drafts review queue AND the
// /drafts/failed screen.
//
// Each selectable row carries a draft_ids[] checkbox; the toolbar holds a
// select-all checkbox, a live selected-count, and one OR MORE submit buttons
// (the review queue has one — "Approve & send selected"; the failed screen has
// two — "Retry selected" + "Discard selected"). This controller keeps them in
// sync entirely on the client: select-all toggles every row checkbox, the count
// + each submit's label reflect the current selection, and every submit is
// disabled until at least one row is chosen. No network calls — the form itself
// posts to the relevant collection action.
export default class extends Controller {
  static targets = ['checkbox', 'selectAll', 'submit', 'count']

  connect() {
    // Capture each submit's base label (the text before the " (N)" count) once,
    // so refresh can rewrite the count without losing "Retry"/"Discard"/etc.
    this.submitTargets.forEach((submit) => {
      submit.dataset.baseLabel = submit.value.replace(/\s*\(\d+\)\s*$/, '')
    })

    // The submits ship statically disabled (server render), so a no-JS page can
    // never post an empty selection; refresh re-establishes the live state once
    // the controller binds. Turbo Stream row removals don't re-run connect() —
    // checkboxTargetDisconnected re-derives the toolbar when a row leaves.
    this.refresh()
  }

  // A bulk action (turbo_stream.remove) takes a row's checkbox target with it.
  // Stimulus fires no connect() on descendant removal, so without this the
  // toolbar keeps a stale count and an enabled submit that posts an empty
  // selection. Re-derive from the surviving checkboxes.
  checkboxTargetDisconnected() {
    this.refresh()
  }

  // Sets every row checkbox to the select-all checkbox's state, then refreshes.
  toggleAll() {
    const checked = this.hasSelectAllTarget ? this.selectAllTarget.checked : false
    this.checkboxTargets.forEach((checkbox) => {
      checkbox.checked = checked
    })
    this.refresh()
  }

  // Recomputes the selected count and writes it into the count label and every
  // submit's label, enabling each submit only when at least one row is selected.
  refresh() {
    const selected = this.checkboxTargets.filter((checkbox) => checkbox.checked).length

    if (this.hasCountTarget) {
      this.countTarget.textContent = `${selected} selected`
    }

    this.submitTargets.forEach((submit) => {
      submit.disabled = selected === 0
      submit.value = `${submit.dataset.baseLabel} (${selected})`
    })

    if (this.hasSelectAllTarget) {
      const total = this.checkboxTargets.length
      this.selectAllTarget.checked = total > 0 && selected === total
    }
  }
}
